import { jsx as _jsx, Fragment as _Fragment, jsxs as _jsxs } from "react/jsx-runtime";
import { allMoves, Color, formatColorString, getLastNode, getPosition, getPositionFromNode, getValidMoves, isCastlingAcrossCheckedSquare, listToNode, Status, } from "@chessalpha/core";
import { Graphics, InteractiveBoard } from "@chessalpha/react";
import { Button, useTheme } from "@mui/material";
import { useAuth } from "@ssf/auth-components";
import { useMutation } from "@tanstack/react-query";
import { useSnackbar } from "notistack";
import { useCallback, useEffect, useRef, useState, } from "react";
import { useTranslation } from "react-i18next";
import { Players, trpcContext } from "../../index.js";
export const getColor = (uid, game) => (uid === game?.white.uid ? Color.white : 0) |
    (uid === game?.black.uid ? Color.black : 0);
export const useNode = (nodes, status) => {
    const [position, setPosition] = useState(getPosition("empty"));
    const [node, setInternalNode] = useState();
    const setNode = useCallback((node) => {
        setInternalNode(node);
        setPosition(getPositionFromNode(node));
    }, []);
    // biome-ignore lint/correctness/useExhaustiveDependencies: Observer
    useEffect(() => {
        if (nodes) {
            setNode(getLastNode(listToNode(nodes)));
        }
    }, [nodes, status, setNode]);
    return { position, setPosition, node, setNode };
};
export const useValidateMove = (color, isMakingMove) => {
    const { t } = useTranslation("useMove");
    const { enqueueSnackbar } = useSnackbar();
    const isValidMove = ({ position, move, }) => {
        const isAllowedToMakeMove = !!((color || 0) & position.toMove);
        const moves = allMoves(position);
        const isValidMove = getValidMoves(position, moves).includes(move);
        const isOppositeMove = allMoves({
            ...position,
            toMove: position.toMove ^ 24,
        }).includes(move);
        if (isOppositeMove) {
            enqueueSnackbar(t("wrongColor", { context: formatColorString(position.toMove ^ 24) }));
        }
        if (isMakingMove) {
            enqueueSnackbar(t("isMakingMove"));
        }
        else if (moves.includes(move)) {
            if (!color) {
                enqueueSnackbar(t("wrongPermissions"));
            }
            else if (!isAllowedToMakeMove) {
                enqueueSnackbar(t("wrongPermissions", {
                    context: formatColorString(position.toMove),
                }));
            }
            if (isAllowedToMakeMove && !isValidMove) {
                enqueueSnackbar(t("illegalMove", {
                    context: isCastlingAcrossCheckedSquare(position, move)
                        ? "castle"
                        : "check",
                }));
            }
        }
        return !isMakingMove && !!((color || 0) & position.toMove) && isValidMove;
    };
    return isValidMove;
};
export const useMove = ({ room, gid, game, position, setPosition, color, disabled = false, }) => {
    const { enqueueSnackbar } = useSnackbar();
    const { signinRedirect } = useAuth();
    const positionRef = useRef(undefined);
    const trpc = trpcContext.useTRPC();
    const { mutate: makeMove, isPending: isMakingMove } = useMutation(trpc.makeMove.mutationOptions({
        onError(error) {
            enqueueSnackbar(error.message, error.data?.code === "UNAUTHORIZED"
                ? {
                    action: (_jsx(Button, { variant: "contained", size: "small", onClick: () => signinRedirect(), children: "Logga in" })),
                }
                : undefined);
            setPosition(positionRef.current ?? position);
        },
    }));
    const isValidMove = useValidateMove(disabled ? undefined : color, isMakingMove);
    const onMove = ({ position: newPosition, move, }) => {
        if (!disabled && (game?.status ?? 0) < Status.ABORTED) {
            positionRef.current = position;
            makeMove({ room, gid, move });
            setPosition({ ...position, board: newPosition.board });
        }
    };
    return { onMove, isValidMove };
};
export const useGame = ({ room, gid, uid, game, nodes, disabled = false, }) => {
    const color = getColor(uid, game);
    const nodeProps = useNode(nodes, game?.status);
    const moveProps = useMove({
        room,
        gid,
        color,
        disabled,
        ...nodeProps,
    });
    const trpc = trpcContext.useTRPC();
    const { mutate: verifyOutOfTime } = useMutation(trpc.verifyOutOfTime.mutationOptions());
    const onOutOfTime = () => {
        if (!disabled) {
            verifyOutOfTime({ room, gid });
        }
    };
    return {
        ...nodeProps,
        ...moveProps,
        onOutOfTime,
        color,
    };
};
export const Game = ({ game, serverTimestamp, size, flipped, color, position, node, onMove, attendances, onOutOfTime, playersProps, ...props }) => (_jsx(InteractiveBoard, { ...props, position: position, move: node?.move, size: size, userColor: color, flipped: flipped, onMove: onMove, SelectedGraphics: game.status >= Status.ABORTED ? () => _jsx(_Fragment, {}) : Graphics, css: { marginTop: "1em", marginBottom: "1em" }, backgroundProps: {
        ...props.backgroundProps,
        css: [
            {
                "> svg": { borderRadius: "0.15em", boxShadow: useTheme().shadows[1] },
            },
            props.backgroundProps?.css,
        ],
        children: (_jsxs(_Fragment, { children: [props.backgroundProps?.children, _jsx(Players, { game,
                    node,
                    position,
                    flipped,
                    serverTimestamp: serverTimestamp ?? node?.timestamp ?? 0,
                    attendances: game.result ? null : attendances,
                    onOutOfTime, ...playersProps })] })),
    } }));
//# sourceMappingURL=Game.js.map